import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  Key,
  Plus,
  Copy,
  RotateCw,
  Trash2,
  Edit,
  Loader2,
  AlertTriangle,
  MoreHorizontal,
  Eye,
  Check,
  X,
  Shield,
  ChevronLeft,
} from "lucide-react";

// ==================== TYPES ====================

interface PartnerApiKey {
  id: string;
  name: string;
  key_prefix: string;
  key_type: string;
  environment: "sandbox" | "production";
  status: "active" | "revoked" | "expired";
  scopes: string[];
  rate_limit_per_minute: number;
  rate_limit_per_day: number;
  ip_allowlist: string[];
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
  updated_at?: string;
  revoked_reason?: string | null;
}

interface KeyLog {
  id: string;
  method: string;
  path: string;
  status_code: number;
  ip_address: string;
  duration_ms: number;
  error_code: string | null;
  idempotency_key: string | null;
  created_at: string;
}

interface KeyLogsResponse {
  logs: KeyLog[];
  total: number;
  limit: number;
  offset: number;
}

// ==================== CONSTANTS ====================

const AVAILABLE_SCOPES = [
  { value: "cards:issue", label: "Issue Cards", description: "Issue new virtual cards" },
  { value: "cards:read", label: "Read Cards", description: "List and retrieve card details" },
  { value: "cards:manage", label: "Manage Cards", description: "Update, freeze, unfreeze, cancel cards" },
  { value: "transactions:read", label: "Read Transactions", description: "View card transaction history" },
  { value: "webhooks:read", label: "Read Webhooks", description: "List webhook endpoints" },
  { value: "webhooks:manage", label: "Manage Webhooks", description: "Create, update, delete webhooks" },
  { value: "automation:execute", label: "Execute Automation", description: "Submit automation flows and retry failed tasks" },
  { value: "automation:read", label: "Read Automation", description: "Poll task status, list tasks and flows" },
];

// ==================== HELPERS ====================

function getStatusBadge(status: string) {
  switch (status) {
    case "active":
      return <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-0">Active</Badge>;
    case "revoked":
      return <Badge className="bg-red-500/10 text-red-600 dark:text-red-400 border-0">Revoked</Badge>;
    case "expired":
      return <Badge variant="secondary">Expired</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function getMethodBadge(method: string) {
  const colors: Record<string, string> = {
    GET: "bg-blue-500/10 text-blue-600",
    POST: "bg-green-500/10 text-green-600",
    PATCH: "bg-amber-500/10 text-amber-600",
    DELETE: "bg-red-500/10 text-red-600",
    PUT: "bg-purple-500/10 text-purple-600",
  };
  return <Badge className={cn("border-0 text-xs font-mono", colors[method] || "")}>{method}</Badge>;
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateTime(dateStr: string | null) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatScopes(scopes: string[]) {
  if (scopes.includes("*")) return <Badge variant="outline" className="text-xs">Full Access</Badge>;
  if (scopes.length <= 2) {
    return scopes.map((s) => (
      <Badge key={s} variant="secondary" className="text-xs mr-1">{s}</Badge>
    ));
  }
  return (
    <>
      <Badge variant="secondary" className="text-xs mr-1">{scopes[0]}</Badge>
      <Badge variant="outline" className="text-xs">+{scopes.length - 1} more</Badge>
    </>
  );
}

// ==================== COMPONENT ====================

export default function ApiKeysPage() {
  const { toast } = useToast();

  // UI state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<PartnerApiKey | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<PartnerApiKey | null>(null);
  const [revokeReason, setRevokeReason] = useState("");
  const [newKeyValue, setNewKeyValue] = useState<string | null>(null);
  const [newKeyName, setNewKeyName] = useState("");
  const [keyCopied, setKeyCopied] = useState(false);
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);
  const [logsOffset, setLogsOffset] = useState(0);
  const LOGS_LIMIT = 50;

  // Create form state
  const [createForm, setCreateForm] = useState({
    name: "",
    environment: "sandbox" as "sandbox" | "production",
    scopes: [] as string[],
    rateLimitPerMinute: 60,
    rateLimitPerDay: 10000,
    ipAllowlist: [] as string[],
    expiresAt: null as string | null,
  });
  const [ipInput, setIpInput] = useState("");

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: "",
    scopes: [] as string[],
    rateLimitPerMinute: 60,
    rateLimitPerDay: 10000,
    ipAllowlist: [] as string[],
    expiresAt: null as string | null,
  });
  const [editIpInput, setEditIpInput] = useState("");

  // ==================== QUERIES ====================

  const { data: keys, isLoading } = useQuery<PartnerApiKey[]>({
    queryKey: ["partner-keys"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/partner-keys");
      return response.json();
    },
  });

  const { data: logsData, isLoading: logsLoading } = useQuery<KeyLogsResponse>({
    queryKey: ["partner-key-logs", selectedKeyId, logsOffset],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        `/api/admin/partner-keys/${selectedKeyId}/logs?limit=${LOGS_LIMIT}&offset=${logsOffset}`
      );
      return response.json();
    },
    enabled: !!selectedKeyId,
  });

  // ==================== MUTATIONS ====================

  const createMutation = useMutation({
    mutationFn: async (data: typeof createForm) => {
      const response = await apiRequest("POST", "/api/admin/partner-keys", data);
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["partner-keys"] });
      setNewKeyValue(data.key);
      setNewKeyName(data.name);
      setIsCreateOpen(false);
      resetCreateForm();
    },
    onError: (error) => {
      toast({ title: "Error", description: String(error), variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await apiRequest("PATCH", `/api/admin/partner-keys/${id}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-keys"] });
      toast({ title: "Success", description: "API key updated" });
      setIsEditOpen(false);
      setEditingKey(null);
    },
    onError: (error) => {
      toast({ title: "Error", description: String(error), variant: "destructive" });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const response = await apiRequest("DELETE", `/api/admin/partner-keys/${id}`, reason ? { reason } : undefined);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-keys"] });
      toast({ title: "Key Revoked", description: "The API key has been permanently revoked" });
      setRevokeTarget(null);
      setRevokeReason("");
    },
    onError: (error) => {
      toast({ title: "Error", description: String(error), variant: "destructive" });
    },
  });

  const rotateMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("POST", `/api/admin/partner-keys/${id}/rotate`);
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["partner-keys"] });
      setNewKeyValue(data.key);
      setNewKeyName(data.name);
    },
    onError: (error) => {
      toast({ title: "Error", description: String(error), variant: "destructive" });
    },
  });

  // ==================== FORM HELPERS ====================

  function resetCreateForm() {
    setCreateForm({
      name: "",
      environment: "sandbox",
      scopes: [],
      rateLimitPerMinute: 60,
      rateLimitPerDay: 10000,
      ipAllowlist: [],
      expiresAt: null,
    });
    setIpInput("");
  }

  function toggleScope(scope: string, formType: "create" | "edit") {
    if (formType === "create") {
      setCreateForm((prev) => ({
        ...prev,
        scopes: prev.scopes.includes(scope)
          ? prev.scopes.filter((s) => s !== scope)
          : [...prev.scopes, scope],
      }));
    } else {
      setEditForm((prev) => ({
        ...prev,
        scopes: prev.scopes.includes(scope)
          ? prev.scopes.filter((s) => s !== scope)
          : [...prev.scopes, scope],
      }));
    }
  }

  function addIp(formType: "create" | "edit") {
    const ip = formType === "create" ? ipInput.trim() : editIpInput.trim();
    if (!ip) return;
    if (formType === "create") {
      if (!createForm.ipAllowlist.includes(ip)) {
        setCreateForm((prev) => ({ ...prev, ipAllowlist: [...prev.ipAllowlist, ip] }));
      }
      setIpInput("");
    } else {
      if (!editForm.ipAllowlist.includes(ip)) {
        setEditForm((prev) => ({ ...prev, ipAllowlist: [...prev.ipAllowlist, ip] }));
      }
      setEditIpInput("");
    }
  }

  function removeIp(ip: string, formType: "create" | "edit") {
    if (formType === "create") {
      setCreateForm((prev) => ({ ...prev, ipAllowlist: prev.ipAllowlist.filter((i) => i !== ip) }));
    } else {
      setEditForm((prev) => ({ ...prev, ipAllowlist: prev.ipAllowlist.filter((i) => i !== ip) }));
    }
  }

  function openEditDialog(key: PartnerApiKey) {
    setEditingKey(key);
    setEditForm({
      name: key.name,
      scopes: [...key.scopes],
      rateLimitPerMinute: key.rate_limit_per_minute,
      rateLimitPerDay: key.rate_limit_per_day,
      ipAllowlist: [...key.ip_allowlist],
      expiresAt: key.expires_at,
    });
    setEditIpInput("");
    setIsEditOpen(true);
  }

  function copyKey() {
    if (newKeyValue) {
      navigator.clipboard.writeText(newKeyValue);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
      toast({ title: "Copied", description: "API key copied to clipboard" });
    }
  }

  // ==================== KEY DETAIL VIEW ====================

  if (selectedKeyId) {
    const selectedKey = keys?.find((k) => k.id === selectedKeyId);
    return (
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setSelectedKeyId(null); setLogsOffset(0); }}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{selectedKey?.name || "API Key Details"}</h1>
            <p className="text-sm text-muted-foreground">
              {selectedKey?.key_prefix}... &middot; {selectedKey?.environment}
            </p>
          </div>
          <div className="ml-auto">{selectedKey && getStatusBadge(selectedKey.status)}</div>
        </div>

        {selectedKey && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-sm text-muted-foreground">Environment</div>
                <div className="font-medium capitalize">{selectedKey.environment}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-sm text-muted-foreground">Rate Limit</div>
                <div className="font-medium">{selectedKey.rate_limit_per_minute}/min</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-sm text-muted-foreground">Daily Limit</div>
                <div className="font-medium">{selectedKey.rate_limit_per_day.toLocaleString()}/day</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-sm text-muted-foreground">Last Used</div>
                <div className="font-medium">{formatDateTime(selectedKey.last_used_at)}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {selectedKey && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Scopes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {selectedKey.scopes.includes("*") ? (
                  <Badge>Full Access (*)</Badge>
                ) : (
                  selectedKey.scopes.map((s) => <Badge key={s} variant="secondary">{s}</Badge>)
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {selectedKey?.ip_allowlist && selectedKey.ip_allowlist.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">IP Allowlist</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {selectedKey.ip_allowlist.map((ip) => (
                  <Badge key={ip} variant="outline" className="font-mono text-xs">{ip}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Request Logs</CardTitle>
            <CardDescription>Recent API requests made with this key</CardDescription>
          </CardHeader>
          <CardContent>
            {logsLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : logsData?.logs && logsData.logs.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Method</TableHead>
                      <TableHead>Path</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logsData.logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{getMethodBadge(log.method)}</TableCell>
                        <TableCell className="font-mono text-xs max-w-[200px] truncate">{log.path}</TableCell>
                        <TableCell>
                          <Badge
                            variant={log.status_code < 400 ? "secondary" : "destructive"}
                            className="text-xs border-0"
                          >
                            {log.status_code}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{log.ip_address}</TableCell>
                        <TableCell className="text-muted-foreground">{log.duration_ms}ms</TableCell>
                        <TableCell className="text-muted-foreground text-xs">{formatDateTime(log.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {logsData.total > LOGS_LIMIT && (
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-muted-foreground">
                      Showing {logsOffset + 1}–{Math.min(logsOffset + LOGS_LIMIT, logsData.total)} of {logsData.total}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={logsOffset === 0}
                        onClick={() => setLogsOffset(Math.max(0, logsOffset - LOGS_LIMIT))}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={logsOffset + LOGS_LIMIT >= logsData.total}
                        onClick={() => setLogsOffset(logsOffset + LOGS_LIMIT)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No request logs yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ==================== MAIN LIST VIEW ====================

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Key className="h-6 w-6" /> API Keys
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage API keys for Partner API access
          </p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" /> Create API Key
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create API Key</DialogTitle>
              <DialogDescription>
                Create a new API key for Partner API access. The key will only be shown once.
              </DialogDescription>
            </DialogHeader>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!createForm.name || createForm.scopes.length === 0) {
                  toast({ title: "Validation Error", description: "Name and at least one scope are required", variant: "destructive" });
                  return;
                }
                createMutation.mutate(createForm);
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="key-name">Name</Label>
                <Input
                  id="key-name"
                  placeholder="e.g., Production Integration"
                  value={createForm.name}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Environment</Label>
                <Select
                  value={createForm.environment}
                  onValueChange={(v) => setCreateForm((prev) => ({ ...prev, environment: v as "sandbox" | "production" }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox (sk_test_)</SelectItem>
                    <SelectItem value="production">Production (sk_live_)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Scopes</Label>
                <div className="space-y-2 border rounded-lg p-3">
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <Checkbox
                      id="scope-all"
                      checked={createForm.scopes.includes("*")}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setCreateForm((prev) => ({ ...prev, scopes: ["*"] }));
                        } else {
                          setCreateForm((prev) => ({ ...prev, scopes: [] }));
                        }
                      }}
                    />
                    <label htmlFor="scope-all" className="text-sm font-medium cursor-pointer">
                      Full Access (*)
                    </label>
                  </div>
                  {AVAILABLE_SCOPES.map((scope) => (
                    <div key={scope.value} className="flex items-start gap-2">
                      <Checkbox
                        id={`scope-${scope.value}`}
                        checked={createForm.scopes.includes("*") || createForm.scopes.includes(scope.value)}
                        disabled={createForm.scopes.includes("*")}
                        onCheckedChange={() => toggleScope(scope.value, "create")}
                      />
                      <label htmlFor={`scope-${scope.value}`} className="cursor-pointer">
                        <div className="text-sm font-medium">{scope.label}</div>
                        <div className="text-xs text-muted-foreground">{scope.description}</div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rate-min">Rate Limit (per min)</Label>
                  <Input
                    id="rate-min"
                    type="number"
                    min={1}
                    value={createForm.rateLimitPerMinute}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, rateLimitPerMinute: parseInt(e.target.value) || 60 }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rate-day">Rate Limit (per day)</Label>
                  <Input
                    id="rate-day"
                    type="number"
                    min={1}
                    value={createForm.rateLimitPerDay}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, rateLimitPerDay: parseInt(e.target.value) || 10000 }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>IP Allowlist (optional)</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g., 203.0.113.45"
                    value={ipInput}
                    onChange={(e) => setIpInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addIp("create");
                      }
                    }}
                  />
                  <Button type="button" variant="outline" size="sm" onClick={() => addIp("create")}>Add</Button>
                </div>
                {createForm.ipAllowlist.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {createForm.ipAllowlist.map((ip) => (
                      <Badge key={ip} variant="secondary" className="gap-1">
                        {ip}
                        <button type="button" onClick={() => removeIp(ip, "create")} className="ml-1 hover:text-destructive">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="expires">Expiry Date (optional)</Label>
                <Input
                  id="expires"
                  type="date"
                  value={createForm.expiresAt ? createForm.expiresAt.split("T")[0] : ""}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      expiresAt: e.target.value ? new Date(e.target.value).toISOString() : null,
                    }))
                  }
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Create Key
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Keys Table — only show active (working) keys */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : !keys || keys.filter((k) => k.status === "active").length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
              <Key className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold">No API Keys</h3>
            <p className="text-muted-foreground text-sm mt-1">Create your first API key to get started with the Partner API</p>
            <Button className="mt-4" onClick={() => setIsCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Create API Key
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Key</TableHead>
                <TableHead>Environment</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Scopes</TableHead>
                <TableHead>Rate Limit</TableHead>
                <TableHead>Last Used</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.filter((k) => k.status === "active").map((key) => (
                <TableRow
                  key={key.id}
                  className="cursor-pointer"
                  onClick={() => setSelectedKeyId(key.id)}
                >
                  <TableCell className="font-medium">{key.name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{key.key_prefix}...</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs capitalize">{key.environment}</Badge>
                  </TableCell>
                  <TableCell>{getStatusBadge(key.status)}</TableCell>
                  <TableCell>{formatScopes(key.scopes)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{key.rate_limit_per_minute}/min</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDateTime(key.last_used_at)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDate(key.created_at)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={() => setSelectedKeyId(key.id)}>
                          <Eye className="h-4 w-4 mr-2" /> View Details
                        </DropdownMenuItem>
                        {key.status === "active" && (
                          <>
                            <DropdownMenuItem onClick={() => openEditDialog(key)}>
                              <Edit className="h-4 w-4 mr-2" /> Edit Settings
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => rotateMutation.mutate(key.id)}>
                              <RotateCw className="h-4 w-4 mr-2" /> Rotate Key
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600 focus:text-red-600"
                              onClick={() => setRevokeTarget(key)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" /> Revoke Key
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit API Key</DialogTitle>
            <DialogDescription>
              Update settings for "{editingKey?.name}"
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!editingKey) return;
              updateMutation.mutate({
                id: editingKey.id,
                data: editForm,
              });
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Scopes</Label>
              <div className="space-y-2 border rounded-lg p-3">
                <div className="flex items-center gap-2 pb-2 border-b">
                  <Checkbox
                    id="edit-scope-all"
                    checked={editForm.scopes.includes("*")}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setEditForm((prev) => ({ ...prev, scopes: ["*"] }));
                      } else {
                        setEditForm((prev) => ({ ...prev, scopes: [] }));
                      }
                    }}
                  />
                  <label htmlFor="edit-scope-all" className="text-sm font-medium cursor-pointer">
                    Full Access (*)
                  </label>
                </div>
                {AVAILABLE_SCOPES.map((scope) => (
                  <div key={scope.value} className="flex items-start gap-2">
                    <Checkbox
                      id={`edit-scope-${scope.value}`}
                      checked={editForm.scopes.includes("*") || editForm.scopes.includes(scope.value)}
                      disabled={editForm.scopes.includes("*")}
                      onCheckedChange={() => toggleScope(scope.value, "edit")}
                    />
                    <label htmlFor={`edit-scope-${scope.value}`} className="cursor-pointer">
                      <div className="text-sm font-medium">{scope.label}</div>
                      <div className="text-xs text-muted-foreground">{scope.description}</div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-rate-min">Rate Limit (per min)</Label>
                <Input
                  id="edit-rate-min"
                  type="number"
                  min={1}
                  value={editForm.rateLimitPerMinute}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, rateLimitPerMinute: parseInt(e.target.value) || 60 }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-rate-day">Rate Limit (per day)</Label>
                <Input
                  id="edit-rate-day"
                  type="number"
                  min={1}
                  value={editForm.rateLimitPerDay}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, rateLimitPerDay: parseInt(e.target.value) || 10000 }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>IP Allowlist</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g., 203.0.113.45"
                  value={editIpInput}
                  onChange={(e) => setEditIpInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addIp("edit");
                    }
                  }}
                />
                <Button type="button" variant="outline" size="sm" onClick={() => addIp("edit")}>Add</Button>
              </div>
              {editForm.ipAllowlist.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {editForm.ipAllowlist.map((ip) => (
                    <Badge key={ip} variant="secondary" className="gap-1">
                      {ip}
                      <button type="button" onClick={() => removeIp(ip, "edit")} className="ml-1 hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-expires">Expiry Date</Label>
              <Input
                id="edit-expires"
                type="date"
                value={editForm.expiresAt ? editForm.expiresAt.split("T")[0] : ""}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    expiresAt: e.target.value ? new Date(e.target.value).toISOString() : null,
                  }))
                }
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Revoke Confirmation Dialog */}
      <AlertDialog open={!!revokeTarget} onOpenChange={(open) => { if (!open) { setRevokeTarget(null); setRevokeReason(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API Key</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently revoke the key "{revokeTarget?.name}" ({revokeTarget?.key_prefix}...).
              Any applications using this key will immediately lose access. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="revoke-reason">Reason (optional)</Label>
            <Input
              id="revoke-reason"
              placeholder="e.g., Compromised, No longer needed"
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => revokeTarget && revokeMutation.mutate({ id: revokeTarget.id, reason: revokeReason || undefined })}
            >
              {revokeMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Revoke Key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* One-Time Key Display Dialog */}
      <Dialog open={!!newKeyValue} onOpenChange={(open) => { if (!open) { setNewKeyValue(null); setKeyCopied(false); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" /> API Key Created
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg border bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-800 dark:text-amber-200">
                  <p className="font-medium">Store this key securely — it will not be shown again.</p>
                  <p className="mt-1 text-amber-700 dark:text-amber-300">
                    If you lose this key, you'll need to rotate it to generate a new one.
                  </p>
                </div>
              </div>
            </div>
            {newKeyName && (
              <div className="text-sm text-muted-foreground">Key name: <span className="font-medium text-foreground">{newKeyName}</span></div>
            )}
            <div className="flex items-center gap-2">
              <code className="flex-1 p-3 bg-muted rounded-lg text-sm font-mono break-all select-all">
                {newKeyValue}
              </code>
              <Button variant="outline" size="icon" onClick={copyKey} className="shrink-0">
                {keyCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => { setNewKeyValue(null); setKeyCopied(false); }}>
              I've saved the key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
